import React, { useEffect, useState } from 'react';
import Layout from '../../components/layout/Layout';
import api from '../../lib/axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Loader2, Plus, Trash2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import DateInput from '../../components/ui/date-input';
import { formatDeNumber } from '@/lib/utils';

const STATUS_LABEL = {
  gemeldet: { label: 'Gemeldet', className: 'bg-amber-100 text-amber-800' },
  genehmigt: { label: 'Genehmigt', className: 'bg-emerald-100 text-emerald-800' },
  abgelehnt: { label: 'Abgelehnt', className: 'bg-rose-100 text-rose-800' },
  abgerechnet: { label: 'Abgerechnet', className: 'bg-slate-200 text-slate-700' },
};

const emptyForm = () => ({
  work_date: new Date().toISOString().slice(0, 10),
  hours: '1.00',
  activity: '',
  notes: '',
});

export default function TeacherPlusHoursPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/teacher/plus-hours');
      setRows(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Plusstunden konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const hours = parseFloat(String(form.hours).replace(',', '.'));
    if (!form.work_date || !form.activity.trim() || !(hours > 0)) {
      toast.error('Datum, Tätigkeit und Stunden (> 0) sind erforderlich');
      return;
    }
    setSaving(true);
    try {
      await api.post('/teacher/plus-hours', {
        work_date: form.work_date,
        hours,
        activity: form.activity.trim(),
        notes: form.notes.trim() || null,
      });
      toast.success('Plusstunden gemeldet');
      setForm(emptyForm());
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Meldung fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const withdraw = async (id) => {
    if (!window.confirm('Offene Meldung zurückziehen?')) return;
    try {
      await api.delete(`/teacher/plus-hours/${id}`);
      toast.success('Meldung zurückgezogen');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  const fmtHours = (h) => formatDeNumber(h);
  const fmtDate = (iso) => {
    if (!iso) return '—';
    const [y, m, d] = String(iso).slice(0, 10).split('-');
    return `${d}.${m}.${y}`;
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 space-y-6 max-w-3xl" data-testid="teacher-plus-hours-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Clock className="h-6 w-6 text-brand-red" /> Plusstunden melden
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Zusätzliche Arbeitszeit außerhalb des Regelunterrichts (Veranstaltungen, Besprechungen, Organisation …).
            Die Verwaltung prüft die Meldung; genehmigte Stunden fließen als Lohn 1 in die Lohnübersicht ein.
          </p>
        </div>

        <Card className="border-slate-200">
          <CardContent className="p-5">
            <form onSubmit={submit} className="space-y-3" data-testid="plus-hours-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500">Datum</label>
                  <DateInput
                    value={form.work_date}
                    onChange={(e) => setForm({ ...form, work_date: e.target.value })}
                    className="mt-1"
                    data-testid="plus-hours-date"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Dauer (Stunden)</label>
                  <Input
                    value={form.hours}
                    onChange={(e) => setForm({ ...form, hours: e.target.value })}
                    placeholder="z. B. 1,5"
                    className="mt-1"
                    data-testid="plus-hours-hours"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Tätigkeit / Begründung</label>
                <Input
                  value={form.activity}
                  onChange={(e) => setForm({ ...form, activity: e.target.value })}
                  placeholder="z. B. Konzertbegleitung, Elternabend, Orga"
                  className="mt-1"
                  data-testid="plus-hours-activity"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Bemerkung (optional)</label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  className="mt-1"
                  data-testid="plus-hours-notes"
                />
              </div>
              <div className="flex justify-end">
                <Button type="submit" disabled={saving} data-testid="plus-hours-submit">
                  {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Plus className="h-4 w-4 mr-1.5" />}
                  Melden
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-0">
            <div className="px-5 py-3 border-b border-slate-100 text-sm font-semibold text-slate-800">
              Meine Meldungen
            </div>
            {loading ? (
              <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></div>
            ) : rows.length === 0 ? (
              <div className="py-10 text-center text-sm text-slate-400">Noch keine Plusstunden gemeldet</div>
            ) : (
              <ul className="divide-y divide-slate-100" data-testid="plus-hours-list">
                {rows.map((r) => {
                  const st = STATUS_LABEL[r.status] || STATUS_LABEL.gemeldet;
                  return (
                    <li key={r.id} className="px-5 py-3 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-slate-900">{fmtDate(r.work_date)}</span>
                          <span className="text-sm tabular-nums text-slate-700">{fmtHours(r.hours)} Std.</span>
                          <Badge className={`${st.className} border-0`}>{st.label}</Badge>
                        </div>
                        <div className="text-sm text-slate-700 mt-0.5">{r.activity}</div>
                        {r.notes && <div className="text-xs text-slate-500 mt-0.5">{r.notes}</div>}
                        {r.review_notes && (
                          <div className="text-xs text-slate-500 mt-1 italic">Verwaltung: {r.review_notes}</div>
                        )}
                      </div>
                      {r.status === 'gemeldet' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => withdraw(r.id)}
                          className="text-slate-400 hover:text-rose-600 shrink-0"
                          data-testid={`plus-hours-delete-${r.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
